import React from 'react';
import { X, Download, Share2, QrCode } from 'lucide-react';
import safeToast from '../utils/safeToast';
import { canonicalCardUrl } from '@/utils/url';

interface QRCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: {
    name: string;
    phone: string;
    email: string;
    website: string;
    // Optional fields present in real user objects; used to build canonical URLs/QRs
    username?: string;
    handle?: string;
  };
}

export const QRCodeModal: React.FC<QRCodeModalProps> = ({ isOpen, onClose, user }) => {
  if (!isOpen) return null;

  // Mode: 'url' QR (online) vs 'vcard' QR (offline-friendly)
  const [mode, setMode] = React.useState<'url' | 'vcard'>('url');

  // Build canonical card URL with ?qr=1 for the URL QR mode
  let cardUrl = '';
  const u: any = user as any;
  const uname = (u && typeof u.username === 'string') ? String(u.username) : '';
  let handle = (u && typeof u.handle === 'string') ? String(u.handle) : '';
  // Derive handle from current hostname if missing (subdomain is the card handle)
  if (!handle && typeof window !== 'undefined') {
    try {
      const host = window.location.hostname;
      const parts = host.split('.');
      // Expect subdomain.tcards.org or subdomain.<custom-domain>
      if (parts.length >= 3) {
        handle = parts[0] || handle;
      }
    } catch {}
  }
  if (uname && handle) {
    cardUrl = canonicalCardUrl(handle, uname);
  } else if (uname && typeof window !== 'undefined') {
    // Fallback: use current origin + /{username}
    try {
      const origin = window.location.origin.replace(/\/$/, '');
      cardUrl = `${origin}/${encodeURIComponent(uname)}`;
    } catch {
      cardUrl = String(user.website || '');
    }
  } else {
    const href = typeof window !== 'undefined' ? window.location.href : String(user.website || '');
    cardUrl = href;
  }
  try {
    const url = new URL(cardUrl);
    url.searchParams.set('qr', '1');
    cardUrl = url.toString();
  } catch {}

  // Build vCard text for offline QR mode (vCard 3.0, CRLF)
  const CRLF = '\r\n';
  const vName = String(user.name || '').trim();
  const [lastName, firstName] = (() => {
    const parts = vName.split(/\s+/);
    const fn = parts.slice(0, -1).join(' ');
    const ln = parts.slice(-1).join('');
    return [ln, fn];
  })();
  const vCompany = String((user as any).company || '').trim();
  const vTitle = String((user as any).position || '').trim();
  const vPhone = String(user.phone || '').trim();
  const vEmail = String(user.email || '').trim();
  const vWebsite = String(user.website || '').trim();
  const vUsername = String((u?.username) || '').trim();
  const vHandle = String((u?.handle) || '').trim();
  const cardOrigin = (vHandle && vUsername) ? canonicalCardUrl(vHandle, vUsername) : cardUrl;
  const vcardText = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `FN:${vName}`,
    `N:${lastName};${firstName};;;`,
    vTitle ? `TITLE:${vTitle}` : '',
    vCompany ? `ORG:${vCompany}` : '',
    vPhone ? `TEL;TYPE=CELL:${vPhone}` : '',
    vEmail ? `EMAIL;TYPE=INTERNET:${vEmail}` : '',
    vWebsite ? `URL:${vWebsite}` : '',
    cardOrigin ? `ITEM1.URL:${cardOrigin}` : '',
    'END:VCARD'
  ].filter(Boolean).join(CRLF);

  // Encode for QR: prefer plain vCard text (many scanners auto-detect and offer "Add Contact").
  // If needed, we can also wrap in a data URL; some readers will open the URL, others will handle the text inline.
  const vcardDataUrl = `data:text/vcard;charset=utf-8,${encodeURIComponent(vcardText)}`;

  // Compute QR img src for the current mode
  const qrData = mode === 'url' ? cardUrl : vcardText;
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrData)}`;

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = qrCodeUrl;
    link.download = `${user.name}-${mode === 'url' ? 'url' : 'vcard'}-qr.png`;
    link.click();
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${user.name} - Digital Card`,
          text: `Open ${user.name}'s card or save the contact`,
          url: cardUrl || window.location.href
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      // Fallback for browsers that don't support Web Share API
      navigator.clipboard.writeText(cardUrl || window.location.href);
      safeToast('success', 'Link copied to clipboard!');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>

        <div className="text-center">
          <h3 className="text-xl font-bold text-gray-900 mb-2">QR Code</h3>
          <p className="text-gray-600 mb-4">Scan to view or save this digital card</p>

          {/* Mode toggle */}
          <div className="inline-flex items-center rounded-xl border border-gray-200 overflow-hidden mb-4">
            <button
              className={`px-3 py-2 text-sm ${mode==='url' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700'}`}
              onClick={() => setMode('url')}
              title="URL QR (opens card with QR landing)"
            >URL QR</button>
            <button
              className={`px-3 py-2 text-sm ${mode==='vcard' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700'}`}
              onClick={() => setMode('vcard')}
              title="Offline vCard QR (scanner can save contact)"
            >Offline vCard QR</button>
          </div>

          <div className="bg-white p-4 rounded-lg border-2 border-gray-100 mb-6 inline-block">
            <img
              src={qrCodeUrl}
              className="w-48 h-48 mx-auto"
            />
          </div>

          {/* Helper note for vCard mode */}
          {mode === 'vcard' && (
            <div className="text-xs text-gray-500 mb-4">
              Many QR scanners recognize vCard text and offer “Add Contact”. If your scanner opens text only, switch to URL QR or long-press to save.
            </div>
          )}

          <div className="flex space-x-3">
            <button
              onClick={handleDownload}
              className="flex-1 flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </button>
            <button
              onClick={handleShare}
              className="flex-1 flex items-center justify-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </button>
          </div>
          {/* Optional: direct open of data URL in vCard mode for some scanners/browsers */}
          {mode === 'vcard' && (
            <div className="mt-3">
              <button
                onClick={() => { try { window.open(vcardDataUrl, '_blank'); } catch {} }}
                className="w-full flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <QrCode className="w-4 h-4 mr-2" />
                Open vCard Data URL
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};